
import { useState, useEffect } from 'react';
import { Transaction } from '@/types/payment';
import { useSafeWebhook } from '@/hooks/useSafeWebhook';

export const usePaymentTransactions = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const webhookContext = useSafeWebhook();

  // Generate a unique ID without relying on external packages
  const generateId = () => {
    return 'id_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
  };
  
  // Load initial data from localStorage on component mount
  useEffect(() => {
    const storedTransactions = localStorage.getItem('transactions');
    if (storedTransactions) {
      setTransactions(JSON.parse(storedTransactions));
    }
  }, []);

  // Update localStorage whenever transactions change
  useEffect(() => {
    localStorage.setItem('transactions', JSON.stringify(transactions));
  }, [transactions]);

  const addTransaction = (transaction: Omit<Transaction, 'id' | 'timestamp'>) => {
    const newTransaction: Transaction = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      ...transaction,
    };
    setTransactions((prevTransactions) => [newTransaction, ...prevTransactions]);
    
    // Trigger webhook if available
    if (webhookContext && typeof webhookContext.triggerWebhook === 'function') {
      webhookContext.triggerWebhook({
        event: 'payment_success',
        data: { 
          amount: transaction.amount, 
          recipientId: transaction.recipient,
          transactionId: newTransaction.id
        }
      });
    }
  };

  return {
    transactions,
    addTransaction,
  };
};
